import * as Yup from 'yup';
import { v4 as uuidV4 } from 'uuid';
import { IMaterial } from './material';
import { IMaterialFormValues } from './materialFormValues';
import { IMaterialUpsert } from './materialUpsert';
import { IMaterialType } from '../materialType/materialType';
import { IPalletBaseType } from '../palletBaseType/palletBaseType';
import { IPack } from '../pack/pack';
import { IUnitOfMeasure } from '../unitOfMeasure/unitOfMeasure';
import MaterialCostModelHelper from '../materialCost/materialCostModelHelper';
import { IOptionType } from '@zz2/zz2-ui';

export default class MaterialModelHelper {
    public static createFormValues = (
        material : IMaterial | null,
        materialTypes : Array<IMaterialType>,
        palletBaseTypes : Array<IPalletBaseType>,
        unitOfMeasures : Array<IUnitOfMeasure>,
        packs : Array<IPack>,
    ) : IMaterialFormValues => {
        if (!material) return {
            id: 0,
            guid: uuidV4(),
            code: '',
            name: '',
            materialType : null,
            palletBaseType : null,
            unitOfMeasure : null,
            packs: [],
            materialCosts: [],
            isInnerPackaging: false,
            isActive: true,
        };

        const materialTypeOption = materialTypes.find(x => x.id === material.materialTypeId);
        const palletBaseTypeOption = palletBaseTypes.find(x => x.id === material.palletBaseTypeId);
        const unitOfMeasureOption = unitOfMeasures.find(x => x.id === material.unitOfMeasureId);

        const packOptions = packs.filter(x => material.packIds.includes(x.id))
            .map((x) => { // TODO: OptionType.FromDataModel
                return {
                    label: `(${x.code}) - ${x.description}`,
                    value: x.id
                };
            });

        const materialCostFormValues = material.materialCosts
            .filter(x => material.materialCostIds.includes(x.id))
            .map((x) => MaterialCostModelHelper.createFormValues(material.id, x))

        return {
            id: material.id,
            guid: material.guid,
            code: material.code ?? '',
            name: material.name ?? '',
            materialType: materialTypeOption
                ? { // TODO: OptionType.FromDataModel
                    label: materialTypeOption.name,
                    value: materialTypeOption.id
                }
                : null,
            palletBaseType: palletBaseTypeOption
                ? { // TODO: OptionType.FromDataModel
                    label: `(${palletBaseTypeOption.code}) - ${palletBaseTypeOption.name}`,
                    value: palletBaseTypeOption.id
                }
                : null,
            unitOfMeasure: unitOfMeasureOption
                ? { // TODO: OptionType.FromDataModel
                    label: `(${unitOfMeasureOption.code}) - ${unitOfMeasureOption.name}`,
                    value: unitOfMeasureOption.id
                }
                : null,
            packs: packOptions,
            materialCosts: materialCostFormValues,
            isInnerPackaging: material.isInnerPackaging,
            isActive: material.isActive,
        };
    };

    public static createUpsert = (formValues : IMaterialFormValues) : IMaterialUpsert => {
        return {
            id: formValues.id,
            guid: formValues.guid,
            code: formValues.code,
            name: formValues.name,
            materialTypeId: Number(formValues.materialType?.value ?? 0),
            unitOfMeasureId: Number(formValues.unitOfMeasure?.value ?? 0),
            palletBaseTypeId: formValues.palletBaseType
                ? Number(formValues.materialType?.value)
                : null,
            packIds: formValues.packs.map(x => Number(x.value)),
            materialCostUpserts: formValues.materialCosts.map(MaterialCostModelHelper.createUpsert),
            isInnerPackaging: formValues.isInnerPackaging,
            isActive: formValues.isActive,
        };
    };

    public static formSchema = () : Yup.ObjectSchema<IMaterialFormValues> => Yup.object().shape({
        id: Yup.number().typeError('Please enter a number.').required('Required'),
        guid: Yup.string().required('Required'),
        code: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        name: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        materialType: Yup.mixed<IOptionType>().nullable().required('Required'),
        unitOfMeasure: Yup.mixed<IOptionType>().nullable().required('Required'),
        palletBaseType: Yup.mixed<IOptionType>().defined().nullable(),
        packs: Yup.array<IOptionType>().defined().nullable().required('Required'),
        materialCosts: Yup.array<IMaterialFormValues>().defined().required('Required'),
        isInnerPackaging: Yup.boolean().required('Required'),
        isActive: Yup.boolean().required('Required'),
    });
}
